/**
 * Login — the end-to-end proof for this scaffold.
 *
 * Posts `{email, password}` to `POST /api/auth/login/`, matching `loginSchema`
 * (refactor/backend/src/modules/users/serializers.ts:207-210). On success the
 * `{refresh, access, user}` body (refactor/backend/src/modules/auth/routes.ts:227-231)
 * is persisted by AuthContext and the user is sent to their role home.
 *
 * Styled per `AuthLayout.tsx`'s top comment: the scoped, non-Carbon
 * "auth-hero" look, not the rest of the app's Carbon components.
 */
import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Email, Locked, Login as LoginIcon, Security } from '@carbon/icons-react';
import { useAuth } from '../../auth/AuthContext';
import { parseApiError } from '../../lib/apiError';
import { AuthField } from '../../components/auth/AuthField';
import { ROUTES, homePathForRole } from '../../routes/paths';

interface LocationState {
  from?: { pathname?: string };
}

export function LoginPage(): React.JSX.Element {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const user = await login(email, password);

      // Prefer the route the user was originally denied (set by RequireAuth),
      // falling back to the role home from app_router.dart:84-90.
      const from = (location.state as LocationState | null)?.from?.pathname;
      navigate(from ?? homePathForRole(user.role), { replace: true });
    } catch (caught) {
      // A wrong password, an unknown email and an inactive account all produce
      // the same 401 "No active account found with the given credentials" —
      // SimpleJWT's single message, preserved by the port (auth/routes.ts:211-213).
      setError(parseApiError(caught).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <span className="auth-hero__badge">Welcome back</span>
      <h1 className="auth-hero__heading">Sign in</h1>
      <p className="auth-hero__subtitle">Enter your details to access your dashboard.</p>

      <form className="auth-hero__form" onSubmit={(event) => void handleSubmit(event)} noValidate>
        {error !== null && (
          <p role="alert" className="auth-hero__error">
            {error}
          </p>
        )}

        <AuthField
          id="login-email"
          label="Email"
          icon={Email}
          type="email"
          name="email"
          autoComplete="email"
          placeholder="you@example.com"
          required
          value={email}
          onChange={setEmail}
        />

        <AuthField
          id="login-password"
          label="Password"
          icon={Locked}
          type="password"
          name="password"
          autoComplete="current-password"
          placeholder="Enter your password"
          required
          value={password}
          onChange={setPassword}
        />

        <button type="submit" className="auth-hero__submit" disabled={submitting}>
          <LoginIcon size={18} aria-hidden="true" />
          {submitting ? 'Signing in…' : 'Sign in securely'}
        </button>
      </form>

      <p className="auth-hero__trust">
        <Security size={16} aria-hidden="true" />
        Your session is protected with secure authentication.
      </p>

      <p className="auth-hero__footer">
        No account? <Link to={ROUTES.register}>Create one</Link>
      </p>
    </>
  );
}

export default LoginPage;
